// securityValidationUtils.ts
// Input validation and sanitization utilities for authentication

/**
 * Credential validation rules and utilities
 */
export const credentialValidation = {
  /**
   * Validates username format and security requirements
   * @param username - The username to validate
   * @returns true if valid, false otherwise
   */
  validateUsername(username: string): boolean {
    if (typeof username !== 'string') return false
    
    const trimmed = username.trim()
    
    // Check length (3-50 characters)
    if (trimmed.length < 3 || trimmed.length > 50) return false
    
    // Allow alphanumeric, dots, underscores, hyphens
    // Prevent special characters that could be used for injection
    const usernameRegex = /^[a-zA-Z0-9._-]+$/
    if (!usernameRegex.test(trimmed)) return false
    
    // Prevent starting/ending with special characters
    if (/^[._-]|[._-]$/.test(trimmed)) return false
    
    // Prevent consecutive special characters
    if (/[._-]{2,}/.test(trimmed)) return false
    
    return true
  },

  /**
   * Validates password strength and security requirements
   * @param password - The password to validate
   * @returns true if valid, false otherwise
   */
  validatePassword(password: string): boolean {
    if (typeof password !== 'string') return false
    
    // Minimum 8 characters, maximum 128 to prevent DoS
    if (password.length < 8 || password.length > 128) return false
    
    // Must contain at least:
    // - 1 lowercase letter
    // - 1 uppercase letter  
    // - 1 number
    // - 1 special character (optional but recommended)
    const hasLowercase = /[a-z]/.test(password)
    const hasUppercase = /[A-Z]/.test(password)
    const hasNumber = /\d/.test(password)
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    
    // Require at least 3 out of 4 character types
    const typeCount = [hasLowercase, hasUppercase, hasNumber, hasSpecialChar].filter(Boolean).length
    
    return typeCount >= 3
  },

  /**
   * Validates display name for user creation
   * @param name - The name to validate
   * @returns true if valid, false otherwise
   */
  validateDisplayName(name: string): boolean {
    if (typeof name !== 'string') return false
    
    const trimmed = name.trim()
    
    // Check length (1-100 characters)
    if (trimmed.length < 1 || trimmed.length > 100) return false
    
    // Allow letters, numbers, spaces, basic punctuation
    // Prevent script injection and special characters
    const nameRegex = /^[a-zA-Z0-9\s\-_.']+$/
    if (!nameRegex.test(trimmed)) return false
    
    // Prevent excessive consecutive spaces
    if (/\s{3,}/.test(trimmed)) return false
    
    return true
  },

  /**
   * Sanitizes input string to prevent XSS and injection attacks
   * @param input - The input string to sanitize
   * @param maxLength - Maximum allowed length (default 1000)
   * @returns Sanitized string
   */
  sanitizeInput(input: string, maxLength: number = 1000): string {
    if (typeof input !== 'string') return ''
    
    return input
      .trim()
      .slice(0, maxLength)
      // Remove potentially dangerous characters
      .replace(/[<>\"'`]/g, '')
      // Remove null bytes and control characters
      .replace(/[\x00-\x1F\x7F]/g, '')
      // Normalize multiple spaces
      .replace(/\s+/g, ' ')
  },

  /**
   * Validates that a string contains no suspicious patterns
   * @param input - The input to check
   * @returns true if safe, false if suspicious
   */
  isInputSafe(input: string): boolean {
    if (typeof input !== 'string') return false
    
    // Check for common injection patterns
    const suspiciousPatterns = [
      /<script\b/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /eval\s*\(/i,
      /expression\s*\(/i,
      /vbscript:/i,
      /data:text\/html/i,
      /\bSELECT\b.*\bFROM\b/i,
      /\bUNION\b.*\bSELECT\b/i,
      /\bINSERT\b.*\bINTO\b/i,
      /\bDELETE\b.*\bFROM\b/i,
      /\bDROP\b.*\bTABLE\b/i
    ]
    
    return !suspiciousPatterns.some(pattern => pattern.test(input))
  }
}

/**
 * Session integrity validation utilities
 */
export const sessionIntegrityValidation = {
  /**
   * Validates session data integrity and consistency
   * @param sessionData - Session data to validate
   * @returns Validation result with details
   */
  validateSessionIntegrity(sessionData: any): SessionIntegrityResult {
    const issues: string[] = []
    let securityScore = 100
    
    // Check if session data exists
    if (!sessionData || typeof sessionData !== 'object') {
      return {
        isValid: false,
        securityScore: 0,
        issues: ['Invalid session data structure'],
        isInitialState: true
      }
    }
    
    // Validate session ID format
    if (sessionData.sessionId) {
      if (typeof sessionData.sessionId !== 'string' || sessionData.sessionId.length < 10) {
        issues.push('Invalid session ID format')
        securityScore -= 20
      }
    }
    
    // Validate login time
    if (sessionData.loginTime) {
      const loginTime = Number(sessionData.loginTime)
      const now = Date.now()
      
      if (isNaN(loginTime) || loginTime <= 0) {
        issues.push('Invalid login time format')
        securityScore -= 15
      } else if (loginTime > now) {
        issues.push('Login time is in the future')
        securityScore -= 25
      } else {
        const sessionAge = now - loginTime
        const maxAge = 24 * 60 * 60 * 1000 // 24 hours
        
        if (sessionAge > maxAge) {
          issues.push('Session has expired')
          securityScore -= 30
        } else if (sessionAge < 1000) {
          issues.push('Session is suspiciously new')
          securityScore -= 5
        }
      }
    }
    
    // Validate key formats
    if (sessionData.publicKey && (typeof sessionData.publicKey !== 'string' || sessionData.publicKey.length < 100)) {
      issues.push('Invalid public key format')
      securityScore -= 15
    }
    
    if (sessionData.secretKey && (typeof sessionData.secretKey !== 'string' || sessionData.secretKey.length < 10)) {
      issues.push('Invalid secret key format')
      securityScore -= 15
    }
    
    if (sessionData.csrfToken && (typeof sessionData.csrfToken !== 'string' || sessionData.csrfToken.length < 10)) {
      issues.push('Invalid CSRF token format')
      securityScore -= 10
    }
    
    // Check for partial session data (potential corruption)
    const requiredFields = ['sessionId', 'loginTime', 'publicKey', 'secretKey']
    const presentFields = requiredFields.filter(field => sessionData[field])
    
    if (presentFields.length > 0 && presentFields.length < requiredFields.length) {
      issues.push('Partial session data detected (possible corruption)')
      securityScore -= 20
    }
    
    securityScore = Math.max(0, Math.min(100, securityScore))
    
    return {
      isValid: issues.length === 0 && securityScore >= 70,
      securityScore,
      issues,
      isInitialState: presentFields.length === 0
    }
  },

  /**
   * Validates encryption key integrity
   * @param publicKey - The public key string to validate
   * @returns true if key appears valid
   */
  validatePublicKeyFormat(publicKey: string): boolean {
    if (typeof publicKey !== 'string') {
      console.debug('Public key validation failed: not a string', typeof publicKey)
      return false
    }
    
    if (publicKey.length < 100) {
      console.debug('Public key validation failed: too short', publicKey.length)
      return false
    }
    
    // Check for PEM format structure
    const pemHeader = '-----BEGIN PUBLIC KEY-----'
    const pemFooter = '-----END PUBLIC KEY-----'
    
    if (!publicKey.includes(pemHeader) || !publicKey.includes(pemFooter)) {
      console.debug('Public key validation failed: missing PEM headers', {
        hasHeader: publicKey.includes(pemHeader),
        hasFooter: publicKey.includes(pemFooter),
        preview: publicKey.substring(0, 50) + '...'
      })
      return false
    }
    
    // Extract the key content
    const keyContent = publicKey
      .replace(pemHeader, '')
      .replace(pemFooter, '')
      .replace(/\s/g, '')
    
    // Basic base64 validation
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
    if (!base64Regex.test(keyContent)) {
      console.debug('Public key validation failed: invalid base64', {
        contentLength: keyContent.length,
        preview: keyContent.substring(0, 20) + '...'
      })
      return false
    }
    
    // Be more lenient with key length - different key sizes are valid
    if (keyContent.length < 200 || keyContent.length > 800) {
      console.debug('Public key validation failed: unexpected length', {
        contentLength: keyContent.length,
        expected: '200-800 characters'
      })
      return false
    }
    
    console.debug('Public key validation passed', {
      totalLength: publicKey.length,
      contentLength: keyContent.length
    })
    
    return true
  }
}

/**
 * Security event logging utilities
 */
export const securityEventLogger = {
  /**
   * Logs security events with proper sanitization
   * @param event - Event type
   * @param details - Event details
   * @param level - Log level (info, warn, error)
   */
  logSecurityEvent(event: string, details: Record<string, any>, level: 'info' | 'warn' | 'error' = 'warn'): void {
    const isDevelopment = process.env.NODE_ENV === 'development'
    
    if (!isDevelopment && level === 'info') {
      return // Don't log info events in production
    }
    
    const timestamp = new Date().toISOString()
    const sanitizedEvent = credentialValidation.sanitizeInput(event, 50)
    
    // Sanitize details to prevent sensitive data logging
    const sanitizedDetails = this.sanitizeLogDetails(details)
    
    const logEntry = {
      timestamp,
      event: sanitizedEvent,
      ...sanitizedDetails
    }
    
    switch (level) {
      case 'error':
        console.error(`[SECURITY ERROR] ${sanitizedEvent}:`, logEntry)
        break
      case 'warn':
        console.warn(`[SECURITY WARNING] ${sanitizedEvent}:`, logEntry)
        break
      case 'info':
        console.info(`[SECURITY INFO] ${sanitizedEvent}:`, logEntry)
        break
    }
  },

  /**
   * Logs failed authentication attempts
   * @param username - The username (will be sanitized)
   * @param reason - Failure reason
   * @param additionalContext - Additional context
   */
  logFailedLogin(username: string, reason: string, additionalContext?: Record<string, any>): void {
    this.logSecurityEvent('FAILED_LOGIN_ATTEMPT', {
      username: this.sanitizeUsername(username),
      reason: credentialValidation.sanitizeInput(reason, 100),
      userAgent: this.sanitizeUserAgent(),
      ...additionalContext
    }, 'warn')
  },

  /**
   * Logs session anomalies
   * @param anomaly - Type of anomaly detected
   * @param sessionInfo - Relevant session information
   */
  logSessionAnomaly(anomaly: string, sessionInfo?: Record<string, any>): void {
    this.logSecurityEvent('SESSION_ANOMALY', {
      anomaly: credentialValidation.sanitizeInput(anomaly, 100),
      timestamp: Date.now(),
      ...this.sanitizeLogDetails(sessionInfo || {})
    }, 'warn')
  },

  /**
   * Logs successful security validations
   * @param validationType - Type of validation performed
   * @param context - Validation context
   */
  logSecurityValidation(validationType: string, context?: Record<string, any>): void {
    this.logSecurityEvent('SECURITY_VALIDATION', {
      validationType: credentialValidation.sanitizeInput(validationType, 50),
      ...this.sanitizeLogDetails(context || {})
    }, 'info')
  },

  /**
   * Sanitizes log details to prevent sensitive data exposure
   */
  sanitizeLogDetails(details: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {}
    
    for (const [key, value] of Object.entries(details)) {
      const sanitizedKey = credentialValidation.sanitizeInput(key, 50)
      
      // Never log sensitive fields
      if (['password', 'secretKey', 'privateKey', 'token', 'key'].some(sensitive => 
        sanitizedKey.toLowerCase().includes(sensitive))) {
        sanitized[sanitizedKey] = '[REDACTED]'
        continue
      }
      
      if (typeof value === 'string') {
        sanitized[sanitizedKey] = credentialValidation.sanitizeInput(value, 200)
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        sanitized[sanitizedKey] = value
      } else if (value !== null && value !== undefined) {
        sanitized[sanitizedKey] = '[OBJECT]'
      }
    }
    
    return sanitized
  },

  /**
   * Sanitizes username for logging
   */
  sanitizeUsername(username: string): string {
    if (!username || typeof username !== 'string') return '[INVALID]'
    
    const sanitized = credentialValidation.sanitizeInput(username, 50)
    
    // Replace middle characters with asterisks for privacy
    if (sanitized.length > 4) {
      return sanitized.slice(0, 2) + '*'.repeat(sanitized.length - 4) + sanitized.slice(-2)
    }
    
    return sanitized.slice(0, 1) + '*'.repeat(Math.max(0, sanitized.length - 1))
  },

  /**
   * Sanitizes user agent for logging
   */
  sanitizeUserAgent(): string {
    const userAgent = navigator?.userAgent || 'Unknown'
    return credentialValidation.sanitizeInput(userAgent, 100)
  }
}

/**
 * Enhanced error handling for authentication operations
 */
export const authErrorHandler = {
  /**
   * Creates a standardized error response
   * @param error - The original error
   * @param context - Additional context
   * @returns Standardized error object
   */
  createSecureError(error: unknown, context: string): SecureAuthError {
    const baseError: SecureAuthError = {
      message: 'Authentication failed',
      code: 'AUTH_ERROR',
      timestamp: Date.now(),
      context: credentialValidation.sanitizeInput(context, 100)
    }
    
    if (error instanceof Error) {
      // Don't expose internal error details in production
      const isDevelopment = process.env.NODE_ENV === 'development'
      
      if (isDevelopment) {
        baseError.details = credentialValidation.sanitizeInput(error.message, 200)
        // Also log the full error for debugging
        console.debug(`Full error details for ${context}:`, {
          name: error.name,
          message: error.message,
          stack: error.stack?.substring(0, 500)
        })
      }
      
      // Classify error types
      if (error.message.includes('network') || error.message.includes('fetch')) {
        baseError.code = 'NETWORK_ERROR'
        baseError.message = 'Network connection failed'
      } else if (error.message.includes('timeout')) {
        baseError.code = 'TIMEOUT_ERROR'
        baseError.message = 'Request timed out'
      } else if (error.message.includes('validation')) {
        baseError.code = 'VALIDATION_ERROR'
        baseError.message = 'Invalid input provided'
      } else if (error.message.includes('decrypt')) {
        baseError.code = 'DECRYPTION_ERROR'
        baseError.message = 'Decryption failed'
      } else if (error.message.includes('key')) {
        baseError.code = 'KEY_ERROR'
        baseError.message = 'Key processing failed'
      }
    } else if (typeof error === 'string') {
      const isDevelopment = process.env.NODE_ENV === 'development'
      if (isDevelopment) {
        baseError.details = credentialValidation.sanitizeInput(error, 200)
      }
    }
    
    return baseError
  },

  /**
   * Handles authentication errors with proper logging and user feedback
   * @param error - The error to handle
   * @param context - Context where the error occurred
   * @param username - Username (for logging, will be sanitized)
   * @returns User-safe error message
   */
  handleAuthError(error: unknown, context: string, username?: string): string {
    const secureError = this.createSecureError(error, context)
    
    // Log the error for monitoring
    securityEventLogger.logFailedLogin(
      username || 'unknown',
      secureError.code,
      { context: secureError.context, errorCode: secureError.code }
    )
    
    // Return user-safe message
    return secureError.message
  }
}

// Type definitions
export interface SessionIntegrityResult {
  isValid: boolean
  securityScore: number
  issues: string[]
  isInitialState: boolean
}

export interface SecureAuthError {
  message: string
  code: string
  timestamp: number
  context: string
  details?: string
}